import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Boxes, Check, Circle } from 'lucide-react';
import { locations as locApi } from '@/api/endpoints';
import { useAuth } from '@/hooks/useAuth';
import { cn, getApiErrorMessage } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from '@/components/ui/link';

// Mirrors the @Size(min, max) constraint on RegisterRequest.password in the backend.
const PASSWORD_MIN_LENGTH = 10;
const PASSWORD_MAX_LENGTH = 128;

export function RegisterPage() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const isPasswordValid = password.length >= PASSWORD_MIN_LENGTH && password.length <= PASSWORD_MAX_LENGTH;
  // Turkish attaches this suffix directly onto the "Terms of Service" link with no
  // separator ("Koşulları'nı kabul ediyorum"); English has no trailing suffix at all.
  const policyAcceptedSuffix = t('auth.policyAcceptedSuffix');

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!policyAccepted || !isPasswordValid) return;
    setError(null);
    setBusy(true);
    try {
      await register({ email, password, firstName, lastName, policyAccepted, marketingConsent });
      try {
        // Every new account starts with zero locations, which otherwise silently
        // blocks the first item from being saved. Best-effort: registration must
        // still succeed if this fails — the zero-locations empty state
        // (Dashboard/ItemsPage/ItemFormPage) is the fallback in that case.
        await locApi.create({ name: t('locations.defaultHomeName'), type: 'HOME', description: null, parentId: null });
        void qc.invalidateQueries({ queryKey: ['locations'] });
        void qc.invalidateQueries({ queryKey: ['stats'] });
      } catch {
        // Swallow: see comment above.
      }
      navigate('/');
    } catch (err) {
      setError(getApiErrorMessage(err, t('auth.registerFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Boxes className="h-6 w-6" />
          </div>
          <CardTitle>{t('auth.register')}</CardTitle>
          <CardDescription>{t('app.tagline')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4" aria-label="register-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="firstName">{t('auth.firstName')}</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  autoComplete="given-name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">{t('auth.lastName')}</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  autoComplete="family-name"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                maxLength={PASSWORD_MAX_LENGTH}
                aria-describedby="password-requirement"
                required
              />
              <p
                id="password-requirement"
                className={cn(
                  'flex items-center gap-1.5 text-xs',
                  isPasswordValid ? 'text-emerald-600 dark:text-emerald-500' : 'text-muted-foreground',
                )}
              >
                {isPasswordValid ? (
                  <Check className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                ) : (
                  <Circle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                )}
                {isPasswordValid
                  ? t('auth.passwordRequirementMet')
                  : t('auth.passwordRequirement', { min: PASSWORD_MIN_LENGTH, max: PASSWORD_MAX_LENGTH })}
              </p>
            </div>
            <div className="space-y-2">
              <label className="flex items-start gap-2 text-sm">
                <input
                  id="policyAccepted"
                  type="checkbox"
                  checked={policyAccepted}
                  onChange={(e) => setPolicyAccepted(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-input"
                  required
                />
                <span>
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer" intent="inline">
                    {t('auth.policyAcceptedPrivacyLink')}
                  </Link>{' '}
                  {t('auth.policyAcceptedAnd')}{' '}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer" intent="inline">
                    {t('auth.policyAcceptedTermsLink')}
                  </Link>
                  {policyAcceptedSuffix || null}
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm">
                <input
                  id="marketingConsent"
                  type="checkbox"
                  checked={marketingConsent}
                  onChange={(e) => setMarketingConsent(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-input"
                />
                <span>{t('auth.marketingConsent')}</span>
              </label>
            </div>
            {error && <div role="alert" className="text-sm text-destructive">{error}</div>}
            <Button type="submit" className="w-full" loading={busy} disabled={!policyAccepted || !isPasswordValid}>
              {t('auth.register')}
            </Button>
          </form>
          <p className="mt-4 text-center text-sm text-muted-foreground">
            {t('auth.haveAccount')}{' '}
            <Link to="/login" intent="inline" className="font-medium">
              {t('auth.login')}
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
